import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from "react"
import { DepositForAccount } from "@mezo/api"

import {
  repayReceiptToken,
  resetDepositFeeDataState,
  resetDepositState,
  resetErrorState,
  RootState,
  selectActiveToken,
  selectAnnualMintingFee,
  selectDepositId,
  selectDepositsWithReceiptTokenMinted,
  selectModalOptions,
  selectTokenBalance,
  setActiveToken,
  setDepositAmount,
  setDepositId,
  setDepositStatus,
  setHasDepositError,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import {
  bigIntToFloat,
  floatToBigInt,
  validateMaximumTokenAmount,
} from "#/shared/utils"

import { Flow } from "../types"

import { useDepositCarousel, useDepositInput } from "./deposit"
import { useFetchWalletBalances } from "./wallet"

const useStbtcModalClose = (
  onClose: () => void,
  setCurrentlySelectedDeposit?: Dispatch<
    SetStateAction<DepositForAccount | undefined>
  >,
  deposits?: DepositForAccount[],
) => {
  const dispatch = useDappDispatch()

  const closeModalHandler = useCallback(() => {
    if (setCurrentlySelectedDeposit && deposits && deposits?.length > 0) {
      setCurrentlySelectedDeposit(deposits[0])
    }
    dispatch(resetDepositState({ flow: Flow.ETHEREUM }))
    dispatch(resetDepositFeeDataState())
    onClose()
  }, [setCurrentlySelectedDeposit, deposits, onClose, dispatch])

  return { closeModalHandler }
}

export const useStbtcManageModal = (isOpen: boolean, onClose: () => void) => {
  const deposits = useDappSelector(selectDepositsWithReceiptTokenMinted)
  const selectedDepositId = useDappSelector(selectDepositId)

  const { allowToChangeDeposit } = useDappSelector(selectModalOptions)

  const dispatch = useDappDispatch()

  const activeDeposit = useMemo(
    () =>
      selectedDepositId
        ? deposits.find((deposit) => deposit.depositId === selectedDepositId)
        : undefined,
    [deposits, selectedDepositId],
  )

  const depositsToShow = useMemo(() => {
    if (allowToChangeDeposit)
      return deposits.filter((deposit) => !!deposit.depositId)

    if (!allowToChangeDeposit && !!activeDeposit) return [activeDeposit]

    return []
  }, [allowToChangeDeposit, deposits, activeDeposit])

  const onDepositCarouselChangeCallback = useCallback(
    (_: number, nextIndex: number) => {
      const { depositId } = depositsToShow[nextIndex]

      dispatch(setDepositId(depositId))
      dispatch(resetErrorState())
    },
    [depositsToShow, dispatch],
  )

  const {
    currentlySelectedDeposit,
    setCurrentlySelectedDeposit,
    onDepositCarouselChange,
  } = useDepositCarousel(depositsToShow, onDepositCarouselChangeCallback)

  useEffect(() => {
    if (isOpen) {
      dispatch(
        resetDepositState({
          flow: Flow.ETHEREUM,
          resetDepositId: false,
        }),
      )
      dispatch(setActiveToken("stBTC"))
      dispatch(setDepositAmount(""))
    }
  }, [isOpen, dispatch, currentlySelectedDeposit])

  const { closeModalHandler } = useStbtcModalClose(
    onClose,
    setCurrentlySelectedDeposit,
    deposits,
  )

  return {
    deposits: depositsToShow,
    currentlySelectedDeposit,
    onDepositCarouselChange,
    closeModal: closeModalHandler,
  }
}

const useStbtcManageInput = (initialTokenAmount?: string) => {
  const activeToken = useDappSelector(selectActiveToken)

  const { depositAmount, tokenDecimals, setDepositTokenAmountCallback } =
    useDepositInput(initialTokenAmount)

  const balance = useDappSelector((state: RootState) =>
    selectTokenBalance(state, activeToken),
  )

  const additionalValidationCallback = (
    input: string,
    _?: string,
    maxTokenAmount?: string,
  ) => {
    const noError = {
      isValid: true,
      errorMessage: "",
    }

    const isLowerOrEqualMaxValue = maxTokenAmount
      ? validateMaximumTokenAmount(
          floatToBigInt(input, tokenDecimals),
          floatToBigInt(maxTokenAmount),
        )
      : true

    const isLowerOrEqualBalance = validateMaximumTokenAmount(
      floatToBigInt(input, tokenDecimals),
      balance,
    )

    if (!isLowerOrEqualMaxValue) {
      return {
        isValid: false,
        errorMessage: `Maximum amount is ${maxTokenAmount}`,
      }
    }

    if (!isLowerOrEqualBalance) {
      return {
        isValid: false,
        errorMessage: "Insufficient funds",
      }
    }

    return noError
  }

  return {
    depositAmount,
    tokenDecimals,
    setDepositTokenAmountCallback,
    additionalValidationCallback,
  }
}

export const useStbtcManageForm = (deposit: DepositForAccount | undefined) => {
  const {
    depositAmount,
    tokenDecimals,
    setDepositTokenAmountCallback,
    additionalValidationCallback,
  } = useStbtcManageInput("")

  const dispatch = useDappDispatch()

  const { fetchWalletBalances } = useFetchWalletBalances()

  const returnReceiptTokenHandler = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      if (!deposit) return

      const { token, depositId, id } = deposit

      if (!depositId) {
        dispatch(setDepositStatus("idle"))
        dispatch(setHasDepositError(true))
        return
      }

      const parsedAmount = floatToBigInt(depositAmount, tokenDecimals)

      dispatch(setDepositStatus("pending"))

      const { payload: repaid } = await dispatch(
        repayReceiptToken({
          amount: parsedAmount,
          token,
          depositId: BigInt(depositId),
          depositUuid: id,
        }),
      )

      if (!repaid) {
        dispatch(setDepositStatus("idle"))
        dispatch(setHasDepositError(true))
        return
      }

      await fetchWalletBalances({ fetchOnChainData: true, refetch: true })

      dispatch(setDepositStatus("completed"))
    },
    [deposit, dispatch, depositAmount, tokenDecimals, fetchWalletBalances],
  )

  return {
    returnReceiptToken: returnReceiptTokenHandler,
    maxReturnAmount: bigIntToFloat(
      BigInt(deposit?.mintedAmount ?? "0"),
      tokenDecimals,
      tokenDecimals,
    ),
    depositAmount,
    tokenDecimals,
    setDepositTokenAmountCallback,
    additionalValidationCallback,
  }
}

export const useStbtcMintInfo = () => {
  const { tBTC: tbtcAnnualFee, WBTC: wbtcAnnualFee } = useDappSelector(
    selectAnnualMintingFee,
  )

  const info = useMemo(
    () => [
      `Current annual minting fee is: ${tbtcAnnualFee}% of deposited tBTC and ${wbtcAnnualFee}% of deposited WBTC, variable, paid at withdrawal`,
      "You can return stBTC at any time, but you must return the full amount in order to withdraw your debt",
    ],
    [tbtcAnnualFee, wbtcAnnualFee],
  )

  return info
}
